import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../../components/public/Seo";
import { getPublicProduct } from "../../lib/catalogApi";

import { addToEdit, removeFromEdit, isInEdit } from "../../state/editStore";
import { getActiveEditId, setActiveEditId } from "../../state/activeEditStore";
import { createEditMeta } from "../../state/editsStore";
import {
  addToBag,
  removeFromBag,
  isInBag,
} from "../../state/bagStore";
import {
  addToFittingQueue,
  removeFromFittingQueue,
  isInFittingQueue,
} from "../../state/fittingRoomQueueStore";

import { normalizeBackendProduct } from "../../catalog/meshResolver";
import { isTryOnAvailable } from "../../catalog/tryOnAvailability";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";

function normalizeImages(product) {
  const gallery = Array.isArray(product?.images)
    ? product.images
        .map((img) => (typeof img === "string" ? img : img?.url))
        .filter(Boolean)
    : [];

  return Array.from(new Set([product?.image_url, ...gallery].filter(Boolean)));
}

function buildDetailProduct(rawProduct) {
  const tryOnProduct = normalizeBackendProduct(rawProduct);
  const images = normalizeImages(rawProduct);

  return {
    ...tryOnProduct,

    rawProduct,

    id: rawProduct?.id,
    title: rawProduct?.title || tryOnProduct.name,
    name: rawProduct?.title || tryOnProduct.name,
    description: rawProduct?.description || "",
    brand: rawProduct?.brand || tryOnProduct.brand || "Unknown Store",

    category: tryOnProduct.category || rawProduct?.category || null,
    color: rawProduct?.color || null,
    material: rawProduct?.material || null,

    price_amount: rawProduct?.price_amount || null,
    price: Number(rawProduct?.price_amount || 0),
    currency: rawProduct?.currency || "NZD",

    productUrl: rawProduct?.product_url || tryOnProduct.productUrl || null,

    // Product UI should only show merchant/catalog imagery.
    // Do not fall back to try-on/mask-derived images here.
    image: images[0] || null,
    images,

    variants: rawProduct?.variants || [],

    tryOn: tryOnProduct,
    tryOnAvailable: isTryOnAvailable(tryOnProduct),
  };
}

function ensureActiveEdit() {
  let id = getActiveEditId();

  if (!id) {
    const meta = createEditMeta({ name: "My Edit" });
    id = meta.id;
    setActiveEditId(id);
  }

  return id;
}

export default function ProductDetailPage() {
  const { productId } = useParams();

  const [rawProduct, setRawProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [toast, setToast] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);

      try {
        const row = await getPublicProduct(productId);
        if (!cancelled) setRawProduct(row);
      } catch (e) {
        console.error("[product detail] failed to load product", e);
        if (!cancelled) setRawProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const product = useMemo(
    () => (rawProduct ? buildDetailProduct(rawProduct) : null),
    [rawProduct]
  );

const images = product?.images || [];
const [imageIndex, setImageIndex] = useState(0);

useEffect(() => {
  setImageIndex(0);
}, [productId]);

const heroImage = images[imageIndex] || null;

  const activeEditId = getActiveEditId();
  const inEdit = product ? isInEdit(product.id, activeEditId) : false;
  const inBag = product ? isInBag(product.id) : false;
  const queued = product ? isInFittingQueue(product.id) : false;

  function showToast(message) {
    setToast(message);
    window.setTimeout(() => setToast(""), 1200);
  }

  function handleSaveToEdit() {
    if (!product) return;

    const editId = ensureActiveEdit();

    if (isInEdit(product.id, editId)) {
      removeFromEdit(product.id, editId);
      showToast("Removed from Edit");
    } else {
      addToEdit(
        {
          kind: "pinned_product",
          productId: product.id,
          title: product.name,
          imageUrl: product.image,
          sourceUrl: product.productUrl || null,
          createdAt: Date.now(),
          category: product.category,
          variant: product.variant || null,
          styleTags: product.styleTags || [],
          fit: product.fit || null,
          budgetTier: product.budgetTier || null,
        },
        editId
      );

      showToast("Saved to Edit");
    }

    setTick((v) => v + 1);
  }

  function handleAddToBag() {
    if (!product) return;

    if (isInBag(product.id)) {
      removeFromBag(product.id);
      showToast("Removed from Bag");
    } else {
      addToBag(product);
      showToast("Added to Bag");
    }

    setTick((v) => v + 1);
  }

  function handleAddToFittingRoom() {
    if (!product || !product.tryOnAvailable) return;

    if (isInFittingQueue(product.id)) {
      removeFromFittingQueue(product.id);
      showToast("Removed from Fitting Room");
    } else {
      addToFittingQueue(product.tryOn);
      showToast("Added to Fitting Room");
    }

    setTick((v) => v + 1);
  }

  void tick;

  if (loading) {
    return <p className="public-muted">Loading product…</p>;
  }

  if (!product) {
    return (
      <>
        <Link to="/browse" className="public-navlink">
          ← Back to Browse
        </Link>
        <p className="public-muted" style={{ marginTop: 20 }}>
          Product not found.
        </p>
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${product.name} — Veya`}
        description={product.description || "View product details on Veya."}
      />

      <div className="public-breadcrumb">
        <Link to="/browse" className="public-navlink">
          Browse
        </Link>
        <span className="public-muted"> / {product.name}</span>
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            top: 18,
            right: 18,
            zIndex: 2000,
            padding: "10px 14px",
            borderRadius: 999,
            background: "rgba(43,43,43,0.88)",
            color: "white",
            fontSize: 13,
          }}
        >
          {toast}
        </div>
      )}

      <section
        className="public-card"
        style={{
          display: "grid",
          gridTemplateColumns:
            window.innerWidth <= 768
              ? "1fr"
              : "minmax(0, 1.1fr) minmax(320px, 0.9fr)",
          gap: window.innerWidth <= 768 ? 18 : 28,
          padding: window.innerWidth <= 768 ? 16 : 24,
          alignItems: "start",
        }}
      >
<div
  style={{
    display: "grid",
    gridTemplateColumns:
  window.innerWidth <= 768 ? "1fr" : "1fr 90px",
    gap: 12,
    alignItems: "start",
  }}
>
  {/* HERO */}
  <div
    style={{
      borderRadius: 22,
      overflow: "hidden",
      background: "rgba(251,247,242,0.7)",
      border: "1px solid rgba(255,255,255,0.6)",
      padding: 8,
    }}
  >
    {heroImage && (
      <img
  src={optimizedImageUrl(heroImage, { width: 1200, quality: 82 })}
  alt={product.name}
  loading="eager"
  decoding="async"
  fetchPriority="high"
        style={{
          width: "100%",
          height: window.innerWidth <= 768 ? 360 : 580,             // ↓ reduced from 720
          objectFit: "contain",     // no crop
          objectPosition: "center",
          display: "block",
        }}
      />
    )}
  </div>

  {/* THUMBS */}
  {images.length > 1 && (
    <div
      style={{
        display: "flex",
        flexDirection: window.innerWidth <= 768 ? "row" : "column",
        gap: 10,
        alignContent: "start",
        overflowX: "auto",
      }}
    >
      {images.map((url, i) => (
        <button
          key={url}
          type="button"
          onClick={() => setImageIndex(i)}
          style={{
            width: 78,
            height: 100,
            borderRadius: 16,
            overflow: "hidden",
            padding: 0,
            cursor: "pointer",
            background: "rgba(251,247,242,0.9)",
            border:
              i === imageIndex
                ? "2px solid var(--burgundy)"
                : "1px solid rgba(255,255,255,0.8)",
          }}
        >
          <img
  src={optimizedImageUrl(url, { width: 180, quality: 72 })}
  alt=""
  loading="lazy"
  decoding="async"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </button>
      ))}
    </div>
  )}
</div>

        <div>
          <div
            style={{
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              opacity: 0.65,
              marginBottom: 8,
            }}
          >
            {product.brand}
          </div>

          <h1
            className="public-h1"
            style={{
              margin: "0 0 6px",
              fontSize: window.innerWidth <= 768 ? 22 : 28,
              lineHeight: 1.2,
            }}
          >
            {product.name}
          </h1>

          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18 }}>
            {product.currency || "NZD"} ${product.price_amount || product.price}
          </div>

 <div
  style={{
display: "grid",
gridTemplateColumns:
  window.innerWidth <= 768
    ? "1fr"
    : "repeat(3, minmax(0, 1fr))",
gap: 10,
    marginBottom: 22,
  }}
>
  <button type="button" className="ghost-btn product-detail-cream-btn" onClick={handleSaveToEdit}>
    {inEdit ? "Saved to Edit" : "Save to Edit"}
  </button>

  <button type="button" className="ghost-btn product-detail-cream-btn" onClick={handleAddToBag}>
    {inBag ? "Remove from Bag" : "Add to Bag"}
  </button>

  {product.tryOnAvailable && (
    <button type="button" className="ghost-btn product-detail-cream-btn" onClick={handleAddToFittingRoom}>
      {queued ? "In Fitting Room" : "Add to Fitting Room"}
    </button>
  )}

  {product.productUrl && (
    <a
      href={product.productUrl}
      target="_blank"
      rel="noreferrer"
      className="ghost-btn product-detail-cream-btn"
      style={{
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      View on Store
    </a>
  )}
</div>

          {product.variants?.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Options</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {product.variants.map((v) => (
                  <span
                    key={v.id || `${v.option1_value}-${v.option2_value}`}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 999,
                      background: "rgba(251,247,242,0.9)",
                      border: "1px solid rgba(43,43,43,0.10)",
                      fontSize: 13,
                    }}
                  >
                    {[v.option1_value, v.option2_value, v.option3_value]
                      .filter(Boolean)
                      .join(" / ")}
                  </span>
                ))}
              </div>
            </div>
          )}

          {product.description && (
            <p
              style={{
                lineHeight: 1.7,
                fontSize: 15,
                opacity: 0.9,
                marginBottom: 22,
              }}
            >
              {product.description}
            </p>
          )}

          <div style={{ display: "grid", gap: 10, marginBottom: 22 }}>
            {product.color && (
              <div>
                <strong>Colour:</strong> {product.color}
              </div>
            )}

            {product.material && (
              <div>
                <strong>Material:</strong> {product.material}
              </div>
            )}

            {product.category && (
              <div>
                <strong>Category:</strong> {product.category}
              </div>
            )}

            {!product.tryOnAvailable && (
              <div className="public-muted" style={{ fontSize: 13 }}>
                Try-on is not available for this item.
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}